// ============================================
// Memory Edit Module - API (ES6)
// 읽기: Firestore 직접 접근
// 쓰기: Cloud Run API (authFetch)
// ============================================

import { authFetch } from '../shared/auth-fetch.js';
import { MEMORY_ENDPOINTS } from '../shared/api-endpoints.js';
import { showToast } from '../utils.js';
import {
  getMemoryDb,
  getCurrentMemorySessionId,
  getCurrentMemoryRoomId,
  getCurrentMemoryUserId,
  setLayerData,
  setLayerLoading,
  addLayerItem,
  updateLayerItem,
  removeLayerItem
} from './state.js';

// ============================================
// Firestore 직접 로드 (읽기 전용)
// ============================================

export async function loadLayerDataFromFirestore(layer) {
  const db = getMemoryDb();
  const userId = getCurrentMemoryUserId();
  const roomId = getCurrentMemoryRoomId();
  const sessionId = getCurrentMemorySessionId();

  if (!userId || !roomId || !sessionId) {
    console.warn('[MemoryAPI] 세션 정보 없음');
    return null;
  }

  setLayerLoading(layer, true);

  try {
    const sessionRef = db.collection('users').doc(userId)
      .collection('chatRooms').doc(roomId)
      .collection('sessions').doc(sessionId);

    let data = null;

    switch (layer) {
      case 'event': {
        const snap = await sessionRef.collection('events').orderBy('turn', 'desc').limit(50).get();
        data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        break;
      }
      case 'quote': {
        const snap = await sessionRef.collection('quotes').orderBy('createdAt', 'desc').limit(50).get();
        data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        break;
      }
      case 'summary': {
        const doc = await sessionRef.collection('memory').doc('summary').get();
        data = doc.exists ? (doc.data()?.current || null) : null;
        break;
      }
    }

    setLayerData(layer, data);
    return data;
  } catch (error) {
    console.error(`[MemoryAPI] ${layer} 로드 오류:`, error);
    showToast('데이터 로드 실패', 'error');
    return null;
  } finally {
    setLayerLoading(layer, false);
  }
}

// ============================================
// 모든 레이어 로드
// ============================================

export async function loadAllLayerData() {
  const layers = ['event', 'quote'];
  await Promise.all(layers.map(layer => loadLayerDataFromFirestore(layer)));
}

// ============================================
// Event CRUD (Cloud Run API)
// ============================================

export async function createEvent(data) {
  const roomId = getCurrentMemoryRoomId();
  const sessionId = getCurrentMemorySessionId();

  if (!roomId || !sessionId) {
    throw new Error('세션 정보가 없습니다');
  }

  const response = await authFetch(MEMORY_ENDPOINTS.createEvent, {
    body: { roomId, sessionId, data }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '사건 생성 실패');
  }

  const result = await response.json();
  const newEvent = { id: result.id, ...result.data };
  addLayerItem('event', newEvent);
  showToast('사건이 추가되었습니다');
  return newEvent;
}

export async function updateEvent(eventId, data) {
  const roomId = getCurrentMemoryRoomId();
  const sessionId = getCurrentMemorySessionId();

  if (!roomId || !sessionId) {
    throw new Error('세션 정보가 없습니다');
  }

  const response = await authFetch(MEMORY_ENDPOINTS.updateEvent, {
    body: { roomId, sessionId, eventId, data }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '사건 업데이트 실패');
  }

  updateLayerItem('event', eventId, data);
  showToast('사건이 업데이트되었습니다');
  return data;
}

export async function deleteEvent(eventId) {
  const roomId = getCurrentMemoryRoomId();
  const sessionId = getCurrentMemorySessionId();

  if (!roomId || !sessionId) {
    throw new Error('세션 정보가 없습니다');
  }

  const response = await authFetch(MEMORY_ENDPOINTS.deleteEvent, {
    body: { roomId, sessionId, eventId }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '사건 삭제 실패');
  }

  removeLayerItem('event', eventId);
  showToast('사건이 삭제되었습니다');
}

// ============================================
// Quote CRUD (Cloud Run API)
// ============================================

export async function createQuote(data) {
  const roomId = getCurrentMemoryRoomId();
  const sessionId = getCurrentMemorySessionId();

  if (!roomId || !sessionId) {
    throw new Error('세션 정보가 없습니다');
  }

  const response = await authFetch(MEMORY_ENDPOINTS.createQuote, {
    body: { roomId, sessionId, data }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '대사 생성 실패');
  }

  const result = await response.json();
  const newQuote = { id: result.id, ...result.data };
  addLayerItem('quote', newQuote);
  showToast('대사가 추가되었습니다');
  return newQuote;
}

export async function updateQuote(quoteId, data) {
  const roomId = getCurrentMemoryRoomId();
  const sessionId = getCurrentMemorySessionId();

  if (!roomId || !sessionId) {
    throw new Error('세션 정보가 없습니다');
  }

  const response = await authFetch(MEMORY_ENDPOINTS.updateQuote, {
    body: { roomId, sessionId, quoteId, data }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '대사 업데이트 실패');
  }

  updateLayerItem('quote', quoteId, data);
  showToast('대사가 업데이트되었습니다');
  return data;
}

export async function deleteQuote(quoteId) {
  const roomId = getCurrentMemoryRoomId();
  const sessionId = getCurrentMemorySessionId();

  if (!roomId || !sessionId) {
    throw new Error('세션 정보가 없습니다');
  }

  const response = await authFetch(MEMORY_ENDPOINTS.deleteQuote, {
    body: { roomId, sessionId, quoteId }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '대사 삭제 실패');
  }

  removeLayerItem('quote', quoteId);
  showToast('대사가 삭제되었습니다');
}

// ============================================
// Summary 업데이트 (Cloud Run API)
// ============================================

export async function updateSummary(summary) {
  const roomId = getCurrentMemoryRoomId();
  const sessionId = getCurrentMemorySessionId();

  if (!roomId || !sessionId) {
    throw new Error('세션 정보가 없습니다');
  }

  const response = await authFetch(MEMORY_ENDPOINTS.updateSummary, {
    body: { roomId, sessionId, summary }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '요약 업데이트 실패');
  }

  const result = await response.json();
  setLayerData('summary', result.data.current);
  showToast('요약이 업데이트되었습니다');
  return result.data.current;
}
